(() => {
  const controllers = new WeakMap();
  const REQUEST = 'Gift card requested';
  const MESSAGE = 'Gift card message';
  const stable = value => JSON.stringify(Object.keys(value || {}).sort().map(key => [key, value[key]]));
  const requested = properties => properties?.[REQUEST] === 'Yes' || (properties?.[REQUEST] !== 'No' && String(properties?.[MESSAGE] || '').trim() !== '');
  const fingerprint = (item, properties = item.properties || {}) => JSON.stringify([String(item.variant_id || item.id), String(item.selling_plan_allocation?.selling_plan?.id || ''), stable(properties)]);
  const quantities = (cart, key, properties) => {
    const result = new Map();
    cart.items.forEach(item => {
      const id = fingerprint(item, item.key === key ? properties : item.properties || {});
      result.set(id, (result.get(id) || 0) + item.quantity);
    });
    return stable(Object.fromEntries(result));
  };

  class GiftCardCart {
    constructor(root) {
      this.root = root;
      this.section = root.dataset.sectionId;
      this.cartURL = new URL(root.dataset.cartUrl, window.location.origin);
      if (this.cartURL.origin !== window.location.origin) return;
      this.ar = root.dataset.locale === 'ar';
      this.busy = false;
      this.failed = false;
      this.stale = false;
      this.bind();
      this.normalize();
    }

    label(key) {
      const labels = {
        saving: ['Saving gift-card choice…', 'جارٍ حفظ اختيار بطاقة الإهداء…'],
        saved: ['Choice saved', 'تم حفظ الاختيار'],
        failed: ['We could not save the gift-card choice. Please retry before checkout.', 'تعذّر حفظ اختيار بطاقة الإهداء. حاول مرة أخرى قبل إتمام الطلب.'],
        quantity: ['Update your cart quantities first, then change the gift-card choice.', 'حدّث كميات السلة أولًا، ثم غيّر اختيار بطاقة الإهداء.'],
        refresh: ['The choice may already be saved. Retry to refresh and verify your cart.', 'قد يكون الاختيار قد حُفظ. حاول مرة أخرى لتحديث السلة والتحقق.']
      };
      return labels[key][this.ar ? 1 : 0];
    }

    bind() {
      controllers.set(this.root, this);
      this.form = this.root.querySelector('[data-cart-gift-form]');
      if (!this.form) return;
      this.choices().forEach(choice => {
        const input = choice.querySelector('[data-gift-card-checkbox]');
        if (input) input.dataset.savedChecked = String(input.checked);
      });
      this.change = event => {
        const input = event.target.closest('[data-gift-card-checkbox]');
        if (!input || !this.root.contains(input)) return;
        const choice = input.closest('[data-gift-card-choice]');
        if (this.busy || this.failed || this.dirtyQuantities()) {
          input.checked = input.dataset.savedChecked === 'true';
          this.feedback(this.label(this.busy ? 'saving' : this.failed ? 'failed' : 'quantity'), this.failed);
          return;
        }
        const restoreFocus = document.activeElement === input;
        this.run(() => this.save(choice.dataset.lineKey, input.checked ? 'Yes' : 'No', restoreFocus));
      };
      this.click = event => {
        if (event.target.closest('[data-gift-card-retry]')) {
          event.preventDefault();
          if (!this.stale && this.dirtyQuantities()) {
            this.feedback(this.label('quantity'));
            return;
          }
          if (!this.busy) this.run(async () => { await this.refresh(); await this.normalizeMissing(); });
        } else if (event.target.closest('.cart-row__remove') && (this.busy || this.stale)) {
          event.preventDefault();
          this.feedback(this.label(this.busy ? 'saving' : 'refresh'), !this.busy);
        }
      };
      this.submit = event => {
        const checkout = event.submitter?.name === 'checkout';
        if (checkout && !this.busy && !this.stale && this.missing().length && this.dirtyQuantities()) {
          event.preventDefault();
          this.feedback(this.label('quantity'));
          return;
        }
        if (this.busy || this.stale || (checkout && (this.failed || this.missing().length))) {
          event.preventDefault();
          this.feedback(this.label(this.busy ? 'saving' : 'failed'), !this.busy);
          this.root.querySelector('[data-gift-card-retry]')?.focus();
        }
      };
      this.root.addEventListener('change', this.change);
      this.root.addEventListener('click', this.click);
      this.form.addEventListener('submit', this.submit);
      this.lock(this.busy);
    }

    unbind() {
      this.root.removeEventListener('change', this.change);
      this.root.removeEventListener('click', this.click);
      this.form?.removeEventListener('submit', this.submit);
      controllers.delete(this.root);
    }

    choices() { return [...this.root.querySelectorAll('[data-gift-card-choice]')]; }
    missing() { return this.choices().filter(choice => !['Yes', 'No'].includes(choice.dataset.request)); }
    dirtyQuantities() {
      return [...this.root.querySelectorAll('.cart-row[data-line-quantity]')].some(row => {
        const input = row.querySelector('[name="updates[]"]');
        return input && String(input.value) !== String(row.dataset.lineQuantity);
      });
    }

    feedback(text = '', retry = false) {
      const node = this.root.querySelector('[data-gift-card-feedback]');
      const content = node?.querySelector('[data-gift-card-feedback-text]');
      const button = node?.querySelector('[data-gift-card-retry]');
      if (content) content.textContent = text;
      if (button) button.hidden = !retry;
      if (node) node.hidden = !text;
    }

    lock(locked) {
      this.root.setAttribute('aria-busy', String(locked));
      this.choices().forEach(choice => {
        const input = choice.querySelector('[data-gift-card-checkbox]');
        if (input) input.disabled = locked || this.failed;
        const status = choice.querySelector('[data-gift-card-status]');
        if (status) status.textContent = locked ? this.label('saving') : '';
      });
      this.root.querySelectorAll('[name="updates[]"],button[name="update"]').forEach(input => { input.disabled = locked || this.stale; });
      this.root.querySelectorAll('button[name="checkout"]').forEach(input => { input.disabled = locked || this.stale || this.failed; });
      this.root.querySelectorAll('.cart-row__remove').forEach(link => {
        if (locked || this.stale) link.setAttribute('aria-disabled', 'true');
        else link.removeAttribute('aria-disabled');
      });
    }

    async response(url, options = {}, format = 'json') {
      const abort = new AbortController();
      const timeout = setTimeout(() => abort.abort(), 15000);
      try {
        const response = await fetch(url, { credentials:'same-origin', cache:'no-store', ...options, signal:abort.signal });
        if (!response.ok) throw new Error('Gift-card cart request failed');
        return await response[format]();
      } finally { clearTimeout(timeout); }
    }

    async snapshot() {
      const cart = await this.response(this.cartURL.pathname + '.js');
      if (!cart || !Array.isArray(cart.items)) throw new Error('Invalid cart response');
      return cart;
    }

    preserveText() {
      const active = document.activeElement;
      const focus = active && this.form.contains(active) && ['note', 'discount'].includes(active.name)
        ? { name:active.name, start:active.selectionStart, end:active.selectionEnd } : null;
      return { fields:[...this.form.querySelectorAll('[name="note"],[name="discount"]')].map(input => [input.name, input.value]), focus };
    }

    hydrate(html) {
      if (typeof html !== 'string' || !html.trim()) throw new Error('Missing cart section response');
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const replacement = [...parsed.querySelectorAll('[data-cart-gift-card]')].find(node => node.dataset.sectionId === this.section);
      if (!replacement?.querySelector('[data-cart-gift-form]') && replacement?.querySelector('.final-cart__empty') === null) throw new Error('Invalid cart section response');
      if (!replacement) throw new Error('Missing cart section');
      const text = this.preserveText();
      this.unbind();
      this.root.replaceWith(replacement);
      this.root = replacement;
      this.bind();
      text.fields.forEach(([name, value]) => {
        const input = this.form?.querySelector('[name="' + name + '"]');
        if (input) input.value = value;
      });
      if (text.focus) {
        const input = this.form?.querySelector('[name="' + text.focus.name + '"]');
        input?.focus();
        if (input && Number.isInteger(text.focus.start) && Number.isInteger(text.focus.end)) input.setSelectionRange?.(text.focus.start, text.focus.end);
      }
      this.stale = false;
    }

    async refresh() {
      const url = new URL(window.location.href);
      if (url.origin !== this.cartURL.origin) throw new Error('Invalid cart origin');
      url.searchParams.set('section_id', this.section);
      this.hydrate(await this.response(url.pathname + url.search, {}, 'text'));
    }

    async save(key, value, restoreFocus = false) {
      if ((value !== null && !['Yes', 'No'].includes(value)) || !key) throw new Error('Invalid gift-card choice');
      const before = await this.snapshot();
      const item = before.items.find(line => line.key === key);
      if (!item) { this.stale = true; throw new Error('Cart line changed'); }
      if (value === null) value = requested(item.properties) ? 'Yes' : 'No';
      const properties = { ...(item.properties || {}), [REQUEST]:value };
      const sectionsURL = new URL(window.location.href);
      this.stale = true;
      const after = await this.response(this.cartURL.pathname + '/change.js', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ id:item.key, quantity:item.quantity, properties, sections:[this.section], sections_url:sectionsURL.pathname + sectionsURL.search })
      });
      this.stale = true;
      if (!Array.isArray(after.items) || quantities(before, key, properties) !== quantities(after) || stable(before.attributes) !== stable(after.attributes) || before.note !== after.note) throw new Error('Cart preservation verification failed');
      if (!after.items.some(line => fingerprint(line) === fingerprint(item, properties) && line.properties?.[REQUEST] === value)) throw new Error('Gift-card save not confirmed');
      if (after.sections?.[this.section]) this.hydrate(after.sections[this.section]);
      else await this.refresh();
      const verified = await this.snapshot();
      if (quantities(after) !== quantities(verified) || !verified.items.some(line => fingerprint(line) === fingerprint(item, properties))) { this.stale = true; throw new Error('Gift-card readback failed'); }
      if (restoreFocus) {
        const matches = verified.items.filter(line => fingerprint(line) === fingerprint(item, properties));
        if (matches.length === 1) this.focusAfterSave = matches[0].key;
      }
    }

    async normalizeMissing() {
      let count = 0;
      while (this.missing().length) {
        if (++count > 100) throw new Error('Gift-card normalization limit');
        const choice = this.missing()[0];
        await this.save(choice.dataset.lineKey, null);
      }
    }

    async run(task) {
      if (this.busy || !this.form) return;
      this.busy = true;
      this.failed = false;
      this.feedback();
      this.lock(true);
      try {
        await task();
        await this.normalizeMissing();
      } catch {
        this.failed = true;
        this.choices().forEach(choice => {
          const input = choice.querySelector('[data-gift-card-checkbox]');
          if (input) input.checked = input.dataset.savedChecked === 'true';
        });
        this.feedback(this.label(this.stale ? 'refresh' : 'failed'), true);
      } finally {
        this.busy = false;
        this.lock(false);
        if (!this.failed && this.focusAfterSave) this.choices().find(choice => choice.dataset.lineKey === this.focusAfterSave)?.querySelector('[data-gift-card-checkbox]')?.focus();
        this.focusAfterSave = null;
      }
    }

    normalize() {
      if (!this.form) return;
      if (this.missing().length && this.dirtyQuantities()) {
        this.feedback(this.label('quantity'));
        this.lock(false);
        return;
      }
      if (this.missing().length) this.run(() => this.normalizeMissing());
      else this.lock(false);
    }
  }

  function init(root) { if (root && !controllers.has(root)) new GiftCardCart(root); }
  document.querySelectorAll('[data-cart-gift-card]').forEach(init);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-cart-gift-card]').forEach(init);
  });
})();
